import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NEWSLETTER_FORM_ID } from "../../config/newsletter.js";

/**
 * THE ONLY newsletter component (§7.6) — EmailOctopus's own hosted-form
 * script embed, not a custom form: EmailOctopus injects its form markup
 * into this container once the script loads. DE and EN each have their
 * own form ID (separate EmailOctopus lists per language). Always placed
 * on a light/cream background (tl-card-light or equivalent) across its
 * call sites — the fallback text color below assumes that.
 */
export default function NewsletterEmbed({ lang }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const loadedFormIdRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const formId = NEWSLETTER_FORM_ID[lang];

  // EmailOctopus's script pulls in its own reCAPTCHA + Google Fonts payload
  // (~450KB combined, mostly unused — confirmed via Lighthouse) on top of
  // itself. Home.jsx renders this mid-page, so most visits never scroll
  // that far during initial load — defer injection until the container is
  // actually about to enter the viewport instead of loading it unconditionally
  // on mount.
  useEffect(() => {
    if (!formId || !containerRef.current || isVisible) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { rootMargin: "200px" }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [formId, isVisible]);

  useEffect(() => {
    if (!formId || !containerRef.current || !isVisible) return undefined;
    // React 18 StrictMode (dev only) mounts, cleans up, then mounts again
    // synchronously to catch non-idempotent effects. Without this guard,
    // the synthetic cleanup ran `replaceChildren()` while EmailOctopus's
    // <script> was still executing/loading, ripping out its own DOM
    // context mid-flight — the script then tried to read its own
    // `parentNode` and crashed ("Cannot read properties of null"). Since
    // the ref persists across that double-invoke, the second mount now
    // sees the same formId already loaded and skips re-injecting.
    if (loadedFormIdRef.current === formId) return undefined;
    containerRef.current.replaceChildren();
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://eomail5.com/form/${formId}.js`;
    script.dataset.form = formId;
    containerRef.current.appendChild(script);
    loadedFormIdRef.current = formId;

    // EmailOctopus's own rendered widget (e.g. an "Interest" multi-select)
    // shows a visible placeholder label but sets no aria-label on either
    // the native <select> or its custom chevron toggle button — patch
    // both from that same visible text once the script renders them, since
    // we don't control the widget's markup directly. Class names are its
    // own generated hashes, so match by substring, not exact class.
    const observer = new MutationObserver(() => {
      const container = containerRef.current;
      if (!container) return;
      container.querySelectorAll('[class*="select-wrapper"]').forEach((wrapper) => {
        const label = wrapper.querySelector('[class*="placeholder"]')?.textContent?.trim();
        if (!label) return;
        const chevron = wrapper.querySelector('[role="button"][class*="chevron"]:not([aria-label])');
        if (chevron) chevron.setAttribute("aria-label", label);
      });
      // The native <select> behind each widget isn't a descendant of its
      // visual wrapper — EmailOctopus renders it as a later sibling under a
      // shared ancestor a few levels up — so walk up from the select
      // instead of assuming direct nesting.
      container.querySelectorAll("select:not([aria-label])").forEach((select) => {
        let node = select.parentElement;
        while (node && node !== container) {
          const label = node.querySelector('[class*="placeholder"]')?.textContent?.trim();
          if (label) {
            select.setAttribute("aria-label", label);
            break;
          }
          node = node.parentElement;
        }
      });
    });
    observer.observe(containerRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [formId, isVisible]);

  if (!formId) {
    return (
      <p style={{ fontSize: "14px", color: "var(--color-ink-soft)", margin: 0 }}>{t("newsletter.notConfigured")}</p>
    );
  }

  return <div ref={containerRef} />;
}
